import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { OWNER_EMAIL } from '../lib/permissions';

const FamilyOfficeContext = createContext(null);

const LS_KEY = 'uh_active_fo_id';

export function FamilyOfficeProvider({ user, businesses, children }) {
  const [familyOffices, setFamilyOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFoId, _setActiveFoId] = useState(null);

  // Subscribe to family_offices collection.
  // Depend on user.uid so the subscription re-fires after login (Firestore
  // rules require request.auth != null, so subscribing before auth is set
  // results in permission-denied and an empty list until refresh).
  useEffect(() => {
    if (!user?.uid) {
      setFamilyOffices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'family_offices'), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.short_code || a.name || '').localeCompare(b.short_code || b.name || ''));
      setFamilyOffices(list);
      setLoading(false);
    }, (err) => {
      console.error('[FamilyOfficeContext] snapshot error:', err);
      setLoading(false);
    });
    return () => unsub();
  }, [user?.uid]);

  // Which FOs can this user access?
  // - OWNER_EMAIL sees all
  // - Others: FOs referenced by businesses where the user has any role
  const accessibleFos = useMemo(() => {
    if (!user) return [];
    if (user.email === OWNER_EMAIL) return familyOffices;
    const foIds = new Set(
      (businesses || [])
        .filter((b) => b.roles && b.roles[user.uid])
        .map((b) => b.family_office_id)
        .filter(Boolean)
    );
    return familyOffices.filter((f) => foIds.has(f.id));
  }, [familyOffices, businesses, user]);

  // Initial active FO from localStorage or first accessible
  useEffect(() => {
    if (accessibleFos.length === 0) return;
    const saved = localStorage.getItem(LS_KEY);
    const valid = saved && accessibleFos.find((f) => f.id === saved);
    _setActiveFoId(valid ? saved : accessibleFos[0].id);
  }, [accessibleFos]);

  const setActiveFoId = (id) => {
    _setActiveFoId(id);
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
  };

  const activeFo = useMemo(
    () => accessibleFos.find((f) => f.id === activeFoId) || null,
    [accessibleFos, activeFoId]
  );

  const value = { familyOffices, accessibleFos, activeFo, activeFoId, setActiveFoId, loading };

  return <FamilyOfficeContext.Provider value={value}>{children}</FamilyOfficeContext.Provider>;
}

export function useFamilyOffice() {
  const ctx = useContext(FamilyOfficeContext);
  if (!ctx) throw new Error('useFamilyOffice must be used within FamilyOfficeProvider');
  return ctx;
}
